import { getSecret, isSecretBound } from "@/lib/platform/secrets";
import { WATCHED_USER_IDS } from "@/lib/telegram/watchlist";
import { swallow } from "@/lib/telemetry/swallow";
import { withTimeout } from "@/lib/timeout";

/**
 * Server-side captures otherwise carry no `$lib` — PostHog's default bot
 * filter (`$virt_is_bot`) treats an absent/unrecognised `$lib` as a strong
 * "Automation" signal, so every `capturePostHogEvent` call (all of
 * `hunt_*`, all `ai_*`) was being classified as bot traffic and silently
 * dropped by any dashboard/insight using the default bot exclusion. See
 * `src/lib/analytics/README.md` for the query-side fix (filter on
 * `capture_surface`, not `$virt_is_bot`).
 *
 * That fix addressed the event PROPERTY; the ingest REQUEST itself still
 * carried no `User-Agent` header at all (this runtime's `fetch()` doesn't
 * set one by default), which is a second, independent input to PostHog's
 * bot classification — every server capture kept getting bucketed as
 * "Automation" traffic even after the `$lib` stamp. A `User-Agent` header of
 * `${SERVER_CAPTURE_LIB}/${SERVER_CAPTURE_LIB_VERSION}` was added to close
 * that gap at the source.
 *
 * IT DID NOT CLOSE IT. Measured in production 2026-08-28, 14 days:
 * `user_signup_completed` (129 events), `apply_intent_clicked` (27) and
 * `feed_loaded` (3,941) all still carried `$virt_traffic_type: "Automation"`
 * and `$virt_is_bot: true`, because `lokerdollar-worker/1` is itself a
 * non-browser UA and PostHog classifies it exactly as it should. Meanwhile
 * the client-side `job_apply_outbound_clicked` was `Regular` / `false`. Net
 * effect: every server-CONFIRMED outcome was excluded from any insight using
 * the default bot filter, while every client-side INTENT event was included
 * — a funnel that could show clicks but never confirmations.
 *
 * The real fix is below: forward the ORIGINATING user's `User-Agent` when the
 * capture happens inside a request scope, and fall back to the worker UA only
 * for cron/queue/script captures, which genuinely are automation. See the
 * `originUserAgent` block in `capturePostHogEvent`.
 *
 * THAT DID NOT CLOSE IT EITHER, on its own. PostHog classifies at query time
 * from the `$raw_user_agent` event PROPERTY; the ingest header is not an
 * input. Request-origin captures kept reading 100% "Automation" until the UA
 * was also stamped as `$raw_user_agent` (2026-09-28).
 */
const SERVER_CAPTURE_LIB = "lokerdollar-worker";
const SERVER_CAPTURE_LIB_VERSION = "1";

/**
 * Fixed, non-secret host/key substituted for a blank `NEXT_PUBLIC_POSTHOG_*`
 * pair ONLY when `E2E_BLOCK_EGRESS=1` (tests/e2e/QA-03: "assert PostHog event
 * emission without a real key"). Every generated preview worker ships with
 * both blank on purpose (`scripts/gen-preview-wrangler.ts`: "Do NOT emit prod
 * analytics from the sandbox"), which means the gate just below this
 * constant returns before `fetch()` is ever called — a deleted
 * `capturePostHogEvent` call and a working one are indistinguishable to any
 * test under those creds, egress guard or not. Falling back to this fixed
 * pair when the flag is set restores the real code path (so a caller bug —
 * wrong event name, wrong properties — still gets exercised) without
 * weakening "never emit real analytics from the sandbox": with
 * `E2E_BLOCK_EGRESS=1`, `src/lib/e2e/egress-guard.ts` unconditionally
 * intercepts any `*.posthog.com` request before it leaves the process, so
 * this never reaches the real PostHog ingest. `E2E_BLOCK_EGRESS` is the same
 * flag the guard itself trusts to never be "1" in production.
 */
const E2E_LEDGER_FALLBACK_HOST = "https://us.i.posthog.com";
const E2E_LEDGER_FALLBACK_KEY = "e2e-ledger-sandbox-key";

/**
 * Wall-clock cap for one PostHog capture POST. Ingest is a small fire-and-forget
 * write; 5s is well past any healthy latency and drops a wedged ingest rather
 * than letting it hold a Worker invocation open.
 */
const POSTHOG_CAPTURE_TIMEOUT_MS = 5_000;

export interface PostHogLikeEnv {
	NEXT_PUBLIC_POSTHOG_HOST?: string;
	NEXT_PUBLIC_POSTHOG_KEY?: string;
	POSTHOG_API_HOST?: string;
	POSTHOG_PROJECT_ID?: string;
	POSTHOG_PERSONAL_API_KEY?: string;
}

/**
 * Name of the cookie posthog-js persists its state in. Server-rendered routes
 * can read this to recover the browser's `distinct_id` so server-emitted events
 * land on the same person timeline as client-side events — without it, server
 * events fall back to a synthetic id (e.g. a job id) and never reconcile into
 * funnels like browse → apply → signup.
 */
export function posthogPersistenceCookieName(
	apiKey: string | undefined,
): string | null {
	return apiKey ? `ph_${apiKey}_posthog` : null;
}

/**
 * Extracts `distinct_id` from the raw value of the posthog-js persistence
 * cookie (a JSON blob). Returns `null` if absent or unparseable.
 */
export function posthogDistinctIdFromCookieValue(
	rawCookieValue: string | undefined,
): string | null {
	if (!rawCookieValue) return null;
	try {
		const parsed = JSON.parse(decodeURIComponent(rawCookieValue)) as {
			distinct_id?: unknown;
		};
		return typeof parsed.distinct_id === "string" && parsed.distinct_id
			? parsed.distinct_id
			: null;
	} catch {
		return null;
	}
}

/**
 * Extracts the active PostHog `$session_id` from the raw value of the posthog-js
 * persistence cookie. posthog-js stores `$sesid` as a tuple
 * `[lastActivityTs, sessionId, sessionStartTs]`; we return the sessionId at
 * index 1. Returns `null` if absent or unparseable.
 *
 * Attaching this to server-emitted events lets analytics filter out traffic that
 * has no browser session (SEO crawlers, stale-param re-renders) when computing
 * human-only rates like the search zero-result rate.
 */
export function posthogSessionIdFromCookieValue(
	rawCookieValue: string | undefined,
): string | null {
	if (!rawCookieValue) return null;
	try {
		const parsed = JSON.parse(decodeURIComponent(rawCookieValue)) as {
			$sesid?: unknown;
		};
		const sesid = parsed.$sesid;
		if (Array.isArray(sesid) && typeof sesid[1] === "string" && sesid[1]) {
			return sesid[1];
		}
		return null;
	} catch {
		return null;
	}
}

/**
 * Distinct-id prefixes this codebase mints from REQUEST data rather than from a
 * browser or a signed-in user — `server-hop-<jobId>`, `gate-skip-<jobId>`,
 * `job-gone-<jobId>`, and the consent beacon's `anon_<uuid>`.
 *
 * Each one is unjoinable by construction: a second visit to the same job mints
 * the same id for a different human, and a second job mints a different id for
 * the same human. Under `person_profiles: "always"` each also became a PostHog
 * person, which is why server captures produced 13,565–20,831 distinct ids per
 * week against 554–1,116 real browser ids (measured 2026-08-27) and every
 * `uniq(person_id)` metric read roughly 20× its true value.
 *
 * The check lives HERE, in the one function every server capture goes through,
 * not at the ~15 call sites — a call site is exactly the place a future
 * synthetic id gets added and the flag gets forgotten. Callers that can do
 * better should still use `resolveServerIdentity` so the event lands on the
 * real person; this is the floor, not the goal.
 */
const SYNTHETIC_DISTINCT_ID_PREFIXES = [
	"server-hop-",
	"gate-skip-",
	"job-gone-",
	"anon_",
] as const;

/**
 * Exact-match literals used as a stand-in id (no per-request suffix, so the
 * prefix list cannot catch them). Left as persons they collapse every
 * cookieless event into one giant fake person.
 */
const SYNTHETIC_DISTINCT_IDS = new Set(["anonymous", "anonymous_magic_link"]);

export function isSyntheticDistinctId(distinctId: string): boolean {
	return (
		SYNTHETIC_DISTINCT_IDS.has(distinctId) ||
		SYNTHETIC_DISTINCT_ID_PREFIXES.some((prefix) =>
			distinctId.startsWith(prefix),
		)
	);
}

/**
 * Resolves the identity a server-side capture should use, in the one order that
 * keeps server events joinable: the signed-in user id, then the browser's own
 * posthog-js `distinct_id` (persistence cookie), then a synthetic fallback.
 *
 * The third case is the one that used to cost us. A synthetic id derived from
 * request data (`server-hop-<jobId>`, `job-gone-<jobId>`, `gate-skip-<jobId>`)
 * mints ONE PostHog person per job id under `person_profiles: "always"` —
 * measured 2026-08-27, server captures produced 13.5k–20.8k distinct ids/week
 * against 554–1,116 real browser ids, so every `uniq(person_id)` metric
 * (retention, WAU, funnel step counts) was reading ~20× its true value.
 * `personless` is the signal to keep the event but suppress the person profile.
 *
 * `sessionId` is returned alongside because a server event with no `$session_id`
 * cannot be stitched into the browser session that caused it — which is why the
 * 30-day seeker funnel is non-monotonic (more people at the apply gate than at
 * the job card that precedes it).
 */
export function resolveServerIdentity(input: {
	userId?: string | null;
	/** Raw value of the posthog-js persistence cookie, if the request carried one. */
	rawCookieValue?: string;
	/** Used only when there is neither a user id nor a browser cookie. */
	fallbackId: string;
}): { distinctId: string; personless: boolean; sessionId: string | null } {
	const browserDistinctId = posthogDistinctIdFromCookieValue(
		input.rawCookieValue,
	);
	const sessionId = posthogSessionIdFromCookieValue(input.rawCookieValue);

	if (input.userId) {
		return { distinctId: input.userId, personless: false, sessionId };
	}
	if (browserDistinctId) {
		return { distinctId: browserDistinctId, personless: false, sessionId };
	}
	return { distinctId: input.fallbackId, personless: true, sessionId };
}

export async function capturePostHogEvent(
	env: PostHogLikeEnv,
	input: {
		distinctId: string;
		event: string;
		properties: Record<string, unknown>;
		/**
		 * Observe-only `classifyTraffic()` verdict class of the originating
		 * request (a `TrafficClass` from `@/lib/security/traffic-class`). When
		 * set, stamped as `properties.traffic_class`. Typed `string` on purpose:
		 * importing the type here would pull the security module graph into
		 * workers/mcp, which imports this file (check:worker-deploy-paths).
		 */
		trafficClass?: string;
		/**
		 * Capture the event without creating or updating a PostHog person
		 * profile (`$process_person_profile: false`). Set this whenever the
		 * distinct id is synthetic — see `resolveServerIdentity`.
		 */
		personless?: boolean;
		/**
		 * Person properties to set on the distinct-id's PostHog person (via the
		 * `$set` directive). Use for durable buyer segmentation — e.g.
		 * `{ buyer_type: "data_buyer" }` — so lifecycle cohorts can target paid
		 * data customers later. Not included in the Telegram notify payload.
		 */
		personProperties?: Record<string, unknown>;
		/**
		 * When true, fan the event out to Telegram in parallel with the
		 * PostHog ingest. Subject to allowlist + cooldown in
		 * `notifyTelegramEvent`. Telemetry failures never throw.
		 */
		notify?: boolean;
		/**
		 * The ORIGINATING request's `User-Agent`, when this capture describes
		 * something a real person just did in a browser (signup, apply intent,
		 * checkout). Threaded in by the caller — the same pattern
		 * `resolveServerIdentity` uses for `rawCookieValue` — rather than read
		 * ambiently, because importing `next/headers` here would pull it into
		 * every zone bundle that touches this module and can opt a statically
		 * rendered page into dynamic rendering.
		 *
		 * Omit it for cron/queue/script captures: those genuinely are
		 * automation and should keep the worker UA and classify as such.
		 */
		userAgent?: string;
		/**
		 * The originating visitor's IP (`cf-connecting-ip`). Sibling of
		 * `userAgent`: the ingest request comes from the worker, so without it
		 * every server event carries the worker's egress IP as `$ip` and
		 * distinct-IP counts collapse to 1. Omit for cron/queue/script captures.
		 */
		ip?: string;
	},
) {
	// See E2E_LEDGER_FALLBACK_HOST/KEY's own doc comment above.
	const isE2ELedgerSandbox = process.env.E2E_BLOCK_EGRESS === "1";

	// E2E test users must never pollute production analytics or Telegram —
	// EXCEPT inside the `E2E_BLOCK_EGRESS=1` sandbox, where that is already
	// structurally impossible: every request this function could go on to
	// issue is intercepted by `src/lib/e2e/egress-guard.ts` before it leaves
	// the process (stubbed for PostHog, hard-rejected for Telegram — see the
	// guard's own "rejects everything else" branch), so nothing ever reaches
	// a real host either way. Keeping this early return there too would just
	// hide a signed-in e2e user's capture from the telemetry ledger QA-03
	// built specifically to prove those calls still fire (checkout-handoff.spec.ts,
	// apply-confirm-loop.spec.ts's authed branches).
	if (input.distinctId.startsWith("e2e_user_") && !isE2ELedgerSandbox) return;

	const host =
		env.NEXT_PUBLIC_POSTHOG_HOST ||
		(isE2ELedgerSandbox ? E2E_LEDGER_FALLBACK_HOST : undefined);
	const apiKey =
		env.NEXT_PUBLIC_POSTHOG_KEY ||
		(isE2ELedgerSandbox ? E2E_LEDGER_FALLBACK_KEY : undefined);

	const tasks: Array<Promise<unknown>> = [];

	if (host && apiKey && host.startsWith("http")) {
		tasks.push(
			(async () => {
				// Server-captured events otherwise inherit the WORKER's IP geo in
				// PostHog (client_country absent), not the requesting user's — every
				// signup/hunt event reads as the Workers region. Attach the real
				// client country when a CF request context is available; no-op
				// (tests, scripts, local dev without CF) otherwise.
				let clientCountry: string | undefined;
				try {
					const { getCloudflareContext } = await import(
						"@opennextjs/cloudflare"
					);
					const { cf } = await getCloudflareContext({ async: true });
					clientCountry = cf?.country as string | undefined;
				} catch {
					// No CF context — leave geo unset rather than guess.
				}

				// The ORIGINATING user-agent, when this capture describes something a
				// real person just did in a browser (signup, apply intent, checkout).
				//
				// PostHog derives `$virt_is_bot` / `$virt_traffic_type` from the raw
				// `User-Agent` on the ingest request. Sending `lokerdollar-worker/1`
				// for everything got every server capture classified
				// `$virt_traffic_type: "Automation"`, `$virt_is_bot: true` — so the
				// default bot exclusion dropped them from insights. Measured
				// 2026-08-28 (14d): `user_signup_completed` 129 events, `$virt_is_bot
				// = true`; `apply_intent_clicked` 27, true; while the CLIENT-side
				// `job_apply_outbound_clicked` was `Regular`/false. Every honest
				// server-confirmed outcome was invisible in the default view while
				// the client-side intent events were fully visible — the funnel could
				// only ever show clicks, never confirmations.
				//
				// Forwarding the caller's own UA is accurate, not spoofing: the event
				// describes that person's action in that browser, and posthog-js
				// would have stamped the identical UA had the event been captured
				// client-side. Cron/queue/script captures pass no `userAgent`, so
				// they keep the worker UA and stay correctly classified as
				// Automation.
				//
				// Threaded in by the caller rather than read from `next/headers`:
				// this module is imported by every zone, and the zone bundles sit at
				// ~zero headroom (see app-zone-ci.yml's cap history), while a
				// `next/headers` import can also opt a statically rendered page into
				// dynamic rendering. `resolveServerIdentity` threads `rawCookieValue`
				// the same way for the same reason.
				const callerUa = input.userAgent?.trim();
				const originUserAgent = callerUa ? callerUa.slice(0, 512) : undefined;
				const callerIp = input.ip?.trim();
				const originIp = callerIp ? callerIp.slice(0, 64) : undefined;

				const baseProperties = input.trafficClass
					? { ...input.properties, traffic_class: input.trafficClass }
					: input.properties;
				const properties = input.personProperties
					? { ...baseProperties, $set: input.personProperties }
					: baseProperties;

				// A caller that passes `personless` has a synthetic distinct id; it
				// must never reach person processing. Spread last so an explicit
				// `personless: true` wins over a stale `$process_person_profile` a
				// caller may have set by hand.
				const personProcessing =
					(input.personless ?? isSyntheticDistinctId(input.distinctId))
						? { $process_person_profile: false }
						: {};

				// A literal stand-in id ("anonymous") is personless above, but every
				// cookieless capture still shared ONE distinct_id: 8,300 events/7d
				// on 'anonymous' (PostHog audit 2026-10-20), so any per-distinct-id
				// count read them as one visitor. Mint a per-capture id instead and
				// keep the original literal queryable as `distinct_id_fallback`.
				// Callers that can do better (one id per request, the browser cookie)
				// should — see `resolveServerIdentity`; this is the floor.
				const literalFallback = SYNTHETIC_DISTINCT_IDS.has(input.distinctId);
				const distinctId = literalFallback
					? `anon_${crypto.randomUUID()}`
					: input.distinctId;

				// Analytics ingest must never outlive the work it describes. Crons
				// capture their completion event before the run row is finalised, so
				// an unbounded POST here held the invocation open until the 30-min
				// stale-run sweeper reaped it — losing the run's whole attribution
				// to save one telemetry event.
				//
				// `withTimeout`, NOT a bare `AbortSignal.timeout`: an abort is a
				// *request* the callee may ignore, so a signal-only cap bounds
				// nothing when the ingest host accepts the connection and then
				// wedges (see the header of `@/lib/timeout` and the 2026-07-18
				// social-render-pending sweep). Most callers — `runDeadman`'s
				// per-streak loop among them — await this with no external race of
				// their own, so the cap has to be enforced here. Orphaning the
				// losing fetch is safe: a dropped analytics event has no side
				// effect worth recovering.
				// `.catch(swallow(...))` below counts a dropped ingest instead of
				// letting it vanish into `Promise.allSettled` uncounted (TA.5,
				// docs/plans/qa-signal-selfrepair-2026-07-28.md) — event loss on this
				// best-effort path finally has a rate, not just a shrug.
				return withTimeout(
					"posthog-capture",
					POSTHOG_CAPTURE_TIMEOUT_MS,
					(signal) =>
						fetch(`${host.replace(/\/$/, "")}/i/v0/e/`, {
							method: "POST",
							signal,
							headers: {
								"Content-Type": "application/json",
								// PostHog's "Automation" bot classification is computed from
								// the raw HTTP `User-Agent` header on the ingest request
								// itself, separately from the `$lib` event property below —
								// `fetch()` on this runtime sends no `User-Agent` by default,
								// so every server capture was ALSO getting bucketed as bot
								// traffic at the request level, not just the property level
								// the `$lib` stamp + README.md query-side workaround covers.
								// An honest, first-party UA (same convention posthog-node
								// itself uses: `<sdk-name>/<version>`) fixes it at the source
								// instead of requiring every downstream query to filter
								// `capture_surface = 'server'`.
								"User-Agent":
									originUserAgent ??
									`${SERVER_CAPTURE_LIB}/${SERVER_CAPTURE_LIB_VERSION}`,
							},
							body: JSON.stringify({
								api_key: apiKey,
								event: input.event,
								distinct_id: distinctId,
								properties: {
									...properties,
									...(literalFallback
										? { distinct_id_fallback: input.distinctId }
										: {}),
									// See SERVER_CAPTURE_LIB header comment — dashboards must
									// filter on this, not the default bot-exclusion, to see
									// this traffic.
									$lib: SERVER_CAPTURE_LIB,
									$lib_version: SERVER_CAPTURE_LIB_VERSION,
									capture_surface: "server",
									// Splits the two kinds of server capture that were
									// previously indistinguishable: one a real person's
									// action carrying their UA, the other a system job that
									// legitimately reads as Automation. Lets a query assert
									// the classification fix actually landed, instead of
									// trusting a code comment the way FIX-6 was trusted.
									capture_origin: originUserAgent ? "request" : "system",
									// THE input PostHog actually classifies on. `$virt_is_bot`
									// / `$virt_traffic_type` are computed at QUERY time from the
									// `$raw_user_agent` event property, not from the ingest
									// request's `User-Agent` header above. With only the header
									// set, `apply_intent_clicked` rows with capture_origin =
									// "request" were still 60/60 "Automation" (14d to
									// 2026-09-28). HogQL: getTrafficType(<Chrome UA>) =
									// "Regular", getTrafficType('') = "Automation". System
									// captures leave it absent and stay Automation.
									...(originUserAgent
										? { $raw_user_agent: originUserAgent }
										: {}),
									...(originIp ? { $ip: originIp } : {}),
									...(clientCountry
										? { client_country: clientCountry, $geoip_disable: true }
										: {}),
									...personProcessing,
								},
							}),
						}),
				).catch(swallow("posthog.capture-drop"));
			})(),
		);
	}

	if (input.notify || WATCHED_USER_IDS.has(input.distinctId)) {
		// Self-contained task: the dynamic import + notify live INSIDE the promise
		// pushed to `tasks`, so a slow/failing Telegram module load can never throw
		// before `Promise.allSettled` and abandon the in-flight PostHog ingest above.
		// Awaiting the import inline (the old shape) lost ~42% of `notify: true`
		// events (e.g. `hunt_apply_approved`) when the isolate was torn down by
		// `ctx.waitUntil` after a redirect — the import rejected, the outer promise
		// rejected, and the already-issued ingest fetch never settled.
		tasks.push(
			(async () => {
				try {
					const { notifyTelegramEvent } = await import(
						"@/lib/telegram/event-notify"
					);
					await notifyTelegramEvent({
						event: input.event,
						distinctId: input.distinctId,
						properties: input.properties,
					});
				} catch {
					// Telemetry/notify failures never throw.
				}
			})(),
		);
	}

	if (tasks.length === 0) return;
	await Promise.allSettled(tasks);
}

function resolvePostHogApiHost(env: PostHogLikeEnv): string | undefined {
	if (env.POSTHOG_API_HOST) {
		return env.POSTHOG_API_HOST.replace(/\/$/, "");
	}

	if (env.NEXT_PUBLIC_POSTHOG_HOST?.includes("posthog.com")) {
		return env.NEXT_PUBLIC_POSTHOG_HOST.replace("https://p.", "https://us.");
	}

	return undefined;
}

/**
 * Whether the READ-side PostHog query API is usable from this worker's env.
 * `runPostHogQuery` silently returns `[]` when any of the three read creds is
 * absent — indistinguishable from "the query matched nothing". Crons that feed
 * pipelines from PostHog data (search-demand-ingest → content strategist) must
 * surface this in their run summary so an env gap on a side worker (the
 * ZERNIO_API_KEY / NEXT_PUBLIC_POSTHOG_* incident class) reads as
 * "misconfigured", not "no demand".
 */
export function isPostHogQueryConfigured(env: PostHogLikeEnv): boolean {
	// Presence-only on purpose: this stays sync for its callers, and a Store
	// binding (`POSTHOG_PERSONAL_API_KEY_STORE`) counts as configured without
	// paying the network read here — `runPostHogQuery` does the actual read.
	return Boolean(
		resolvePostHogApiHost(env) &&
			env.POSTHOG_PROJECT_ID &&
			isSecretBound(env, "POSTHOG_PERSONAL_API_KEY"),
	);
}

/** Error-message marker for a bound-but-unresolvable read credential. */
export const POSTHOG_CREDENTIAL_UNRESOLVED = "PostHog credential unresolved";

export async function runPostHogQuery(
	env: PostHogLikeEnv,
	query: string,
): Promise<unknown[]> {
	const apiHost = resolvePostHogApiHost(env);
	const projectId = env.POSTHOG_PROJECT_ID;
	// Store → worker env → process.env via the accessor. Gated on presence so a
	// worker that legitimately holds no read creds keeps returning `[]` quietly
	// (the documented contract above) instead of raising an `unresolved` signal
	// on every call; a bound-but-empty Store entry still reports.
	const keyBound = isSecretBound(env, "POSTHOG_PERSONAL_API_KEY");
	const personalApiKey = keyBound
		? await getSecret(env, "POSTHOG_PERSONAL_API_KEY")
		: undefined;

	// Bound but resolved to nothing is an OUTAGE of the read path, not "no
	// data". 2026-10-11/12: a broken Secrets Store read on backend made this
	// return [] for ~13h and every PostHog-backed liveness row and weekly-growth
	// number read 0 instead of blind. Throw so readers report it.
	if (keyBound && !personalApiKey) {
		throw new Error(
			`${POSTHOG_CREDENTIAL_UNRESOLVED}: POSTHOG_PERSONAL_API_KEY is bound on this worker but resolved to nothing (Secrets Store read failed or entry empty)`,
		);
	}

	if (!apiHost || !projectId || !personalApiKey) {
		return [];
	}

	const response = await fetch(`${apiHost}/api/projects/${projectId}/query`, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${personalApiKey}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			query: {
				kind: "HogQLQuery",
				query,
			},
		}),
	});

	if (!response.ok) {
		throw new Error(
			`PostHog query failed (${response.status}): ${await response.text()}`,
		);
	}

	const body = (await response.json()) as {
		results?: unknown[];
		columns?: string[];
		query_status?: {
			results?: unknown[];
			columns?: string[];
		};
	};

	const rows = body.results ?? body.query_status?.results;
	// A reply with no result array is not "zero rows": it is a stub (the
	// dry-run fetch guard answers `{ok:true,shadow:true}`), a proxy page, or an
	// API change. Reading it as [] made every PostHog-backed liveness row read 0
	// in `ops:run-cron --dry-run` on 2026-10-27. Fail loudly instead.
	if (!Array.isArray(rows)) {
		throw new Error(
			`PostHog query returned no results array (keys: ${Object.keys(body).join(",") || "none"})`,
		);
	}
	return rows;
}

/**
 * Host + project + bearer for hand-rolled READ calls (e.g. the experiments
 * API) that `runPostHogQuery`'s HogQL-only shape cannot make. Same creds and
 * same unresolved-credential contract; null when the worker holds none.
 */
export async function resolvePostHogReadAuth(
	env: PostHogLikeEnv,
): Promise<{ apiHost: string; projectId: string; key: string } | null> {
	const apiHost = resolvePostHogApiHost(env);
	const projectId = env.POSTHOG_PROJECT_ID;
	if (
		!apiHost ||
		!projectId ||
		!isSecretBound(env, "POSTHOG_PERSONAL_API_KEY")
	) {
		return null;
	}
	const key = await getSecret(env, "POSTHOG_PERSONAL_API_KEY");
	if (!key) {
		throw new Error(
			`${POSTHOG_CREDENTIAL_UNRESOLVED}: POSTHOG_PERSONAL_API_KEY is bound but resolved to nothing`,
		);
	}
	return { apiHost, projectId, key };
}

export function escapeHogQlString(value: string): string {
	return value.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
}
